import { useEffect, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import moment from "moment";
import { useParams } from "react-router-dom";
import { isAccessAllowed } from "../../../utils/helpers";
import makeRequest from "../../../api";
import {
  getAllAssessmentQuestionsList,
  getUserNutritionAssessmentResponseList,
} from "../../../api/services/assessment";
import {
  assessmentService,
  nutritionApiService,
  nutritionService,
  userService,
} from "../../../api/services";
import {
  EXC_CATEGORIES,
  EXC_DISHES,
  INC_CATEGORIES,
  INC_DISHES,
  defaultFormState,
  defaultOptionsStatic,
  extraCalorieIntakeQuestionsTitle,
  inputTypeSingleSelect,
  inputTypeText,
} from "./constants";
import { AssesmentProps } from "./types";
import { toast } from "react-toastify";

const AssessmentQuestions = ({
  healthGoal,
  numberOfWeeks,
  setUserResponses,
  mealPlanStartDate,
  issueBasedPlanStatus,
}: AssesmentProps) => {
  const params = useParams();
  const userId = params.userId;
  const [viewFixed, setViewFixed] = useState(true);
  const [allQuestions, setAllQuestions] = useState<any>([]);
  const [allQuestionsMap, setAllQuestionsMap] = useState<any>({});
  const [defaultOptions, setDefaultOptions] = useState(defaultOptionsStatic);
  const [inputData, setInputData] = useState<any>(defaultFormState);

  const [values, setValues] = useState<any>(defaultFormState);
  const [includeDishOptions, setIncludeDishOptions] = useState<string[]>([]);
  const [excludeDishOptions, setExcludeDishOptions] = useState<string[]>([]);
  const [selectedIncludeDishOptions, setSelectedIncludeDishOptions] = useState<
    string[]
  >([]);
  const [selectedExcludeDishOptions, setSelectedExcludeDishOptions] = useState<
    string[]
  >([]);
  const [searchIncludeDishInput, setSearchIncludeDishInput] = useState("");
  const [searchExcludeDishInput, setSearchExcludeDishInput] = useState("");

  const [selectedIncludeDishCategories, setSelectedIncludeDishCategories] =
    useState<string[]>([]);
  const [selectedExcludeDishCategories, setSelectedExcludeDishCategories] =
    useState<string[]>([]);

  const [dishCategories, setDishCategories] = useState<string[]>([]);

  const [snacks, setSnacks] = useState<string[]>([]);
  const [breakfast, setBreakfast] = useState<string[]>([]);
  const [lunch, setLunch] = useState<string[]>([]);
  const [dinner, setDinner] = useState<string[]>([]);
  const [allMealsQuestions, setAllMealsQuestions] = useState<any>({
    snacks: [],
    breakfast: [],
    lunch: [],
    dinner: [],
  });
  const [cuisineTypeRegion, setCuisineTypeRegion] = useState("");
  const [userHasDiabetesCondition, setUserHasDiabetesCondition] =
    useState(false);
  const selectedState = values?.selected_state?.value;
  const healthCondition = values?.conditions_nutrition || [];

  const [regionalStapleOptions, setRegionalStapleOptions] = useState<any>([]);
  const [mealStapleOptions, setMealStapleOptions] = useState<any>([]);
  const [retakeAllowed, setRetakeAllowed] = useState<any>(true);

  const cuisineType =
    values?.cuisine_types?.value === "veg"
      ? "veg_region_cuisine"
      : values?.cuisine_types?.value === "egg"
      ? "egg_region_cuisine"
      : "nonveg_region_cuisine";

  const issueBasedGoals = ["DIABETES_REVERSAL", "PRE_DIABETES_REVERSAL"];

  const isAssessmentQuestionEditAllowed = isAccessAllowed("ASSESSMENT_EDIT");
  const isRegenerateMealPlanAllowed = isAccessAllowed(
    "MEAL_PLAN_REGENERATE_BUTTON"
  );
  const isRetakeAssesmentButtonViewAllowed = isAccessAllowed(
    "RETAKE_ASSESSMENT_BUTTON_VIEW"
  );

  useEffect(() => {
    getCategoryList();
  }, []);

  useEffect(() => {
    const getNames = async (name: string) => {
      let arr: any = allMealsQuestions[name]?.map((item: any) => item?.trim());
      try {
        if (arr && arr.length > 0) {
          const res = await makeRequest(
            nutritionApiService.getRecipeNameFromRecipeIds({
              recipeIds: arr,
            })
          );
          const data = res?.data?.data;
          if (name === "snacks") {
            setSnacks([...data]);
          } else if (name === "breakfast") {
            setBreakfast([...data]);
          } else if (name === "lunch") {
            setLunch([...data]);
          } else if (name === "dinner") {
            setDinner([...data]);
          } else if (name === "lunch_dinner") {
            setLunch([...data]);
          }
        }
      } catch (error: any) {
        toast.error("Error fetching recipe category list!");
      }
    };

    Object.keys(allMealsQuestions)?.forEach((item: any) => {
      getNames(item);
    });
  }, [allMealsQuestions]);

  useEffect(() => {
    let snacks: any, breakfast: any, lunch: any, dinner: any;
    allQuestions?.forEach((item: any) => {
      if (item?.questionCode?.startsWith("snacks_")) {
        snacks = item?.response?.split(",");
      } else if (item?.questionCode?.startsWith("breakfast_")) {
        breakfast = item?.response?.split(",");
      } else if (item?.questionCode?.startsWith("lunch_dinner_")) {
        lunch = item?.response?.split(",");
      } else if (item?.questionCode?.startsWith("lunch_")) {
        lunch = item?.response?.split(",");
      } else if (item?.questionCode?.startsWith("dinner_")) {
        dinner = item?.response?.split(",");
      }
    });
    setAllMealsQuestions(() => {
      return {
        ...allMealsQuestions,
        snacks: snacks,
        breakfast: breakfast,
        lunch_dinner: lunch,
        lunch: lunch,
        dinner: dinner,
      };
    });
  }, [allQuestions]);

  const getCategoryList = async () => {
    try {
      const res = await makeRequest(
        nutritionApiService.getRecipeCategoryList()
      );
      const data = res?.data?.data;

      const formattedOptions = data?.map((item: any) => ({
        value: item,
        label: item,
      }));

      setDishCategories(formattedOptions);
    } catch (error: any) {
      toast.error("Error fetching recipe category list!");
    }
  };

  useEffect(() => {
    const fetchDishes = async () => {
      try {
        if (searchIncludeDishInput?.length > 2) {
          const res = await makeRequest(
            nutritionApiService.getRecipeListFromSearchedTerm({
              text: searchIncludeDishInput,
            })
          );
          const data = res?.data?.data;

          const formattedOptions = data?.map((item: any) => ({
            value: item?.name,
            label: item?.name,
          }));

          setIncludeDishOptions(formattedOptions);
        }
      } catch (error: any) {
        toast.error("Error fetching recipe list!");
      }
    };

    fetchDishes();
  }, [searchIncludeDishInput]);

  useEffect(() => {
    const fetchDishes = async () => {
      try {
        if (searchExcludeDishInput?.length > 2) {
          const res = await makeRequest(
            nutritionApiService.getRecipeListFromSearchedTerm({
              text: searchExcludeDishInput,
            })
          );
          const data = res?.data?.data;

          const formattedOptions = data?.map((item: any) => ({
            value: item?.name,
            label: item?.name,
          }));

          setExcludeDishOptions(formattedOptions);
        }
      } catch (error: any) {
        toast.error("Error fetching recipe list!");
      }
    };

    fetchDishes();
  }, [searchExcludeDishInput]);

  useEffect(() => {
    const hasDiabetesCondition =
      Array.isArray(healthCondition) &&
      healthCondition?.filter(
        (item: any) =>
          item?.value === "Diabetes" || item?.value === "Pre-diabetes"
      )?.length > 0;

    const isDiabetesUser = isIssueBasedGoal() || hasDiabetesCondition;
    setUserHasDiabetesCondition(isDiabetesUser);
  }, [healthCondition, healthGoal]);

  useEffect(() => {
    if (!!selectedState) {
      const state = selectedState;
      if (userHasDiabetesCondition) {
        getRegionalStaple("DR0,DR1");
      } else {
        getRegionalStaple("RS", state);
      }
    }
  }, [selectedState, userHasDiabetesCondition]);

  useEffect(() => {
    if (userHasDiabetesCondition) {
      getMealStaples("DR0,DR1");
    } else {
      getMealStaples("CS0,CS1,OS0,OS1");
    }
  }, [healthGoal, userHasDiabetesCondition]);

  const getRegionalStaple = async (staples: string, state?: string) => {
    try {
      const query = {
        staples: staples,
        ...(state && state?.length > 0 && { state }),
      };

      const res = await makeRequest(
        nutritionApiService.getRegionalStaples(query)
      );
      const data =
        res?.data?.data?.map((item: any) => ({
          value: item?._id,
          label: item?.name,
        })) || [];
      setRegionalStapleOptions([...data]);
    } catch (error: any) {
      toast.error(error?.message);
    }
  };

  const getMealStaples = async (staples: any) => {
    try {
      const query = {
        staples,
      };
      const res = await makeRequest(
        nutritionApiService.getRegionalStaples(query)
      );
      const data =
        res?.data?.data?.map((item: any) => ({
          value: item?._id,
          label: item?.name,
        })) || [];
      setMealStapleOptions([...data]);
    } catch (error: any) {
      toast.error(error?.message);
    }
  };

  const handleIncludeDishSelectChange = (selectedValues: string[]) => {
    setSelectedIncludeDishOptions(selectedValues);
  };

  const handleExcludeDishSelectChange = (selectedValues: string[]) => {
    setSelectedExcludeDishOptions(selectedValues);
  };

  const handleIncludeDishCategorySelectChange = (selectedValues: string[]) => {
    setSelectedIncludeDishCategories(selectedValues);
  };

  const handleExcludeDishCategorySelectChange = (selectedValues: string[]) => {
    setSelectedExcludeDishCategories(selectedValues);
  };

  useEffect(() => {
    let obj = JSON.parse(JSON.stringify(inputData));
    Object.keys(obj)?.forEach((item: string) => {
      var arr = inputData[item]?.optionsSelected?.map((x: any) => {
        if (item === INC_DISHES) {
          return { label: x, value: x };
        } else if (item === INC_CATEGORIES) {
          const y = dishCategories?.filter((a: any) => {
            return a?.value === x;
          });
          return y?.[0];
        } else if (item === EXC_DISHES) {
          return { label: x, value: x };
        } else if (item === EXC_CATEGORIES) {
          const y = dishCategories?.filter((a: any) => {
            return a?.value === x;
          });
          return y?.[0];
        } else if (item === "selected_state") {
          const y = defaultOptions[
            `states_${cuisineTypeRegion}_options`
          ]?.filter((a: any) => {
            return a?.value === x;
          });
          return y?.[0];
        } else if (item === "selected_global") {
          const y = defaultOptions["global_region_options"]?.filter(
            (a: any) => {
              return a?.value === x;
            }
          );
          return y?.[0];
        } else if (item === "secondary_region" || item === "tertiary_region") {
          const y = defaultOptions["veg_region_cuisine_options"]?.filter(
            (a: any) => {
              return a?.value === x;
            }
          );
          return y?.[0];
        } else if (item === "regional_staple_preference") {
          // const y = regionalStapleOptions?.find((a: any) => a?.value === x);
          // return y;
          return {
            value: x,
            label: x,
          };
        } else if (
          item === "staple_preference_lunch" ||
          item === "staple_preference_dinner"
        ) {
          const y = mealStapleOptions?.find((a: any) => a?.value === x);
          return y;
        } else {
          const y = defaultOptions[`${item}_options`]?.filter((a: any) => {
            return a?.value === x;
          });
          return y?.[0];
        }
      });
      if (inputTypeText[item]) {
        obj[item] = inputData?.[item]?.response;
      } else if (inputTypeSingleSelect[item]) {
        if (arr?.length > 0) {
          obj[item] = arr[0];
        } else {
          obj[item] = {};
        }
      } else {
        obj[item] = arr ?? [];
      }
      if (item === INC_DISHES) {
        setSelectedIncludeDishOptions(arr);
      } else if (item === INC_CATEGORIES) {
        setSelectedIncludeDishCategories(arr);
      } else if (item === EXC_DISHES) {
        setSelectedExcludeDishOptions(arr);
      } else if (item === EXC_CATEGORIES) {
        setSelectedExcludeDishCategories(arr);
      }
    });
    setValues({ ...obj });
  }, [inputData]);

  useEffect(() => {
    if (
      values?.regional_staple_preference &&
      Array.isArray(values?.regional_staple_preference)
    ) {
      const regionalStaples = values?.regional_staple_preference?.map(
        (item: any) => {
          const y = regionalStapleOptions?.find(
            (a: any) => a?.value === item?.value
          );
          return y;
        }
      );
      setValues((prev: any) => ({
        ...prev,
        regional_staple_preference: regionalStaples,
      }));
    }
  }, [regionalStapleOptions]);

  const getQuestions = async () => {
    try {
      const res = await makeRequest(
        getUserNutritionAssessmentResponseList(
          {
            healthGoal,
            gabit: "NUTRITION",
          },
          userId
        )
      );
      const response = res?.data?.responseList;
      appendIncludeExcludeResponses(response);

      setAllQuestions([...response]);

      const x = JSON.parse(JSON.stringify(inputData));
      response?.forEach((item: any) => {
        if (
          item?.questionCode === "veg_region_cuisine" ||
          item?.questionCode === "egg_region_cuisine" ||
          item?.questionCode === "nonveg_region_cuisine"
        ) {
          setCuisineTypeRegion(item?.optionsSelected?.[0]);
          const regions = item?.optionsSelected;
          if (regions?.length > 1) {
            x["secondary_region"] = {
              ...item,
              response: regions[1],
              optionsSelected: [regions[1]],
            };
          }
          if (regions?.length > 2) {
            x["tertiary_region"] = {
              ...item,
              response: regions[2],
              optionsSelected: [regions[2]],
            };
          }
        }

        if (
          item?.questionCode === "states_north" ||
          item?.questionCode === "states_south" ||
          item?.questionCode === "states_east" ||
          item?.questionCode === "states_west"
        ) {
          x["selected_state"] = {
            ...item,
            response: JSON.parse(item?.response)?.state,
            optionsSelected:
              JSON.parse(item?.response)?.state !== ""
                ? [JSON.parse(item?.response)?.state]
                : [],
          };
          x["selected_global"] = {
            ...item,
            response: JSON.parse(item?.response)?.global,
            optionsSelected:
              JSON.parse(item?.response)?.global !== ""
                ? [JSON.parse(item?.response)?.global]
                : [],
          };
        } else if (item?.questionCode in inputData) {
          x[item?.questionCode] = item;
        }
      });
      setInputData(JSON.parse(JSON.stringify(x)));
      setUserResponses(JSON.parse(JSON.stringify(x)));
    } catch (error: any) {
      toast.error("Error while fetching users assessment response!");
    }
  };

  const getOptionsList = async () => {
    try {
      const res = await makeRequest(getAllAssessmentQuestionsList());
      const x = { ...defaultOptions };
      let questionsMap = {};
      res?.data?.forEach((item: any) => {
        questionsMap = { ...questionsMap, [item?.questionCode]: item };
        if (
          item?.questionCode === "states_north" ||
          item?.questionCode === "states_south" ||
          item?.questionCode === "states_east" ||
          item?.questionCode === "states_west"
        ) {
          const stateOptions = item?.options?.filter(
            (item: any) => JSON.parse(item)?.key === "state"
          );
          const globalOptions = item?.options?.filter(
            (item: any) => JSON.parse(item)?.key === "global"
          );
          x[`${item?.questionCode}_options`] = JSON.parse(
            stateOptions
          )?.options?.map((item: any) => ({
            label: item?.text,
            value: item?.value,
          }));
          x["global_region_options"] = JSON.parse(globalOptions)?.options?.map(
            (item: any) => ({
              label: item?.text,
              value: item?.value,
            })
          );
        } else if (
          Object.keys(defaultOptionsStatic).includes(
            `${item?.questionCode}_options`
          )
        ) {
          const options: any = [];
          item?.options?.forEach((a: any) => {
            const parsed = JSON.parse(a);
            options?.push({
              label: parsed?.title,
              value: parsed?.optionKey,
            });
          });

          x[`${item?.questionCode}_options`] = options;
        }
      });
      setAllQuestionsMap({ ...questionsMap });
      setDefaultOptions((prev: any) => ({ ...prev, ...x }));
    } catch (error: any) {
      toast.error("Error while fetching all assessment questions!");
    }
  };

  useEffect(() => {
    if (userId && healthGoal) {
      (async () => {
        await getOptionsList();
        getQuestions();
      })();
    }
  }, [userId, healthGoal]);

  const appendIncludeExcludeResponses = (responseList: any) => {
    let date = moment().format("YYYY-MM-DD");
    let res = responseList?.filter(
      (item: any) => item?.questionCode === INC_DISHES
    );
    if (res?.length === 0) {
      let obj = {
        creationDate: date,
        creationTimestamp: date,
        isUserAttribute: false,
        multipleSelect: true,
        optionsSelected: [],
        questionCode: INC_DISHES,
        title: "What dishes to be included for mealplan ?",
        userId: userId,
        vertical: "COMMON#NUTRITION",
      };
      responseList?.push(obj);
    }

    res = responseList?.filter(
      (item: any) => item?.questionCode === INC_CATEGORIES
    );
    if (res?.length === 0) {
      let obj = {
        creationDate: date,
        creationTimestamp: date,
        isUserAttribute: false,
        multipleSelect: true,
        optionsSelected: [],
        questionCode: INC_CATEGORIES,
        title: "What dish categories to be included?",
        userId: userId,
        vertical: "COMMON#NUTRITION",
      };
      responseList?.push(obj);
    }

    res = responseList?.filter(
      (item: any) => item?.questionCode === EXC_DISHES
    );
    if (res?.length === 0) {
      let obj = {
        creationDate: date,
        creationTimestamp: date,
        isUserAttribute: false,
        multipleSelect: true,
        optionsSelected: [],
        questionCode: EXC_DISHES,
        title: "What dishes to be excluded for mealplan?",
        userId: userId,
        vertical: "COMMON#NUTRITION",
      };
      responseList?.push(obj);
    }

    res = responseList?.filter(
      (item: any) => item?.questionCode === EXC_CATEGORIES
    );
    if (res?.length === 0) {
      let obj = {
        creationDate: date,
        creationTimestamp: date,
        isUserAttribute: false,
        multipleSelect: true,
        optionsSelected: [],
        questionCode: EXC_CATEGORIES,
        title: "What dish categories to be excluded from mealplan?",
        userId: userId,
        vertical: "COMMON#NUTRITION",
      };
      responseList?.push(obj);
    }
  };

  const handleChange = (data: any, name: any) => {
    const key = name as string;
    if (name === "selected_state" && data !== null) {
      setValues((prev: any) => ({
        ...prev,
        [name]: data,
        regional_staple_preference: [],
      }));
    } else if (name === "conditions_nutrition") {
      const hasDiabetesCaseBefore =
        values[name]?.filter(
          (item: any) =>
            item?.value === "Diabetes" || item?.value === "Pre-diabetes"
        )?.length > 0;
      const hasDiabetesCaseAfter =
        data?.filter(
          (item: any) =>
            item?.value === "Diabetes" || item?.value === "Pre-diabetes"
        )?.length > 0;
      setValues((prev: any) => ({
        ...prev,
        [name]: data,
        ...(hasDiabetesCaseBefore !== hasDiabetesCaseAfter && {
          regional_staple_preference: [],
        }),
        ...(hasDiabetesCaseBefore !== hasDiabetesCaseAfter && {
          staple_preference_dinner: [],
        }),
        ...(hasDiabetesCaseBefore !== hasDiabetesCaseAfter && {
          staple_preference_lunch: [],
        }),
      }));
    } else if (
      data === null &&
      (inputTypeSingleSelect[key] ||
        name === "veg_region_cuisine" ||
        name === "egg_region_cuisine" ||
        name === "nonveg_region_cuisine")
    ) {
      setValues((prev: any) => ({
        ...prev,
        [name]: {},
      }));
    } else {
      setValues((prev: any) => ({
        ...prev,
        [name]: data,
      }));
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    let data = [];
    let hasMealPlanTypeKey = false;
    let hasExtraCalorieKeys = false;
    let hasLowCarbMealKey = false;
    let hasPreWorkoutMealKey = false;
    let hasStateCuisineKey = false;
    let hasConditionNutritionKey = false;
    let hasItemsToAvoidKey = false;
    let hasItemsToAvoidForVegKey = false;
    let hasItemsToAvoidForNonVegKey = false;
    let hasWorkoutIntensityQuesKey = false;
    let hasWorkoutDurationQuesKey = false;
    let hasWorkoutTypeQuesKey = false;
    let hasRegionalStapleKey = false;
    let hasStaplePreferenceLunchKey = false;
    let hasStaplePreferenceDinnerKey = false;
    let hasGutIssuesKey = false;
    let hasGutTriggersKey = false;

    for (let i = 0; i < allQuestions?.length; ++i) {
      let item = allQuestions[i];
      let d = moment().format("YYYY-MM-DD");

      if (item?.questionCode === "gut_issues") {
        hasGutIssuesKey = true;
      }
      if (item?.questionCode === "gut_food_triggers") {
        hasGutTriggersKey = true;
      }

      if (item?.questionCode === "workout_intensity") {
        hasWorkoutIntensityQuesKey = true;
      }
      if (item?.questionCode === "workout_type") {
        hasWorkoutTypeQuesKey = true;
      }
      if (item?.questionCode === "workout_duration") {
        hasWorkoutDurationQuesKey = true;
      }

      if (
        item?.questionCode === "mealplan_type_lunch" ||
        item?.questionCode === "mealplan_type_dinner"
      ) {
        hasMealPlanTypeKey = true;
      }
      if (
        item?.questionCode === "extra_calorie_tea_coffee" ||
        item?.questionCode === "extra_calorie_protein_supplement" ||
        item?.questionCode === "extra_calorie_dessert_nightbinge" ||
        item?.questionCode === "extra_calorie_others"
      ) {
        hasExtraCalorieKeys = true;
      }

      if (item?.questionCode === "low_carb_meal") {
        hasLowCarbMealKey = true;
      }

      if (item?.questionCode === "conditions_nutrition") {
        hasConditionNutritionKey = true;
      }

      if (
        item?.questionCode === "pre_workout_slot" ||
        item?.questionCode === "pre_workout_snacks"
      ) {
        hasPreWorkoutMealKey = true;
      }

      if (item?.questionCode === "items_to_avoid") {
        hasItemsToAvoidKey = true;
      }
      if (item?.questionCode === "items_to_avoid_for_veg") {
        hasItemsToAvoidForVegKey = true;
      }
      if (item?.questionCode === "items_to_avoid_for_non_veg") {
        hasItemsToAvoidForNonVegKey = true;
      }
      if (
        item?.questionCode === "use_regional_staple" ||
        item?.questionCode === "regional_staple_preference"
      ) {
        hasRegionalStapleKey = true;
      }
      if (item?.questionCode === "staple_preference_lunch") {
        hasStaplePreferenceLunchKey = true;
      }
      if (item?.questionCode === "staple_preference_dinner") {
        hasStaplePreferenceDinnerKey = true;
      }

      if (
        item?.questionCode === "states_north" ||
        item?.questionCode === "states_south" ||
        item?.questionCode === "states_east" ||
        item?.questionCode === "states_west"
      ) {
        hasStateCuisineKey = true;
        data.push({
          ...item,
          response: JSON.stringify({
            state: values?.selected_state?.value,
            global: values?.selected_global?.value,
          }),
          creationDate: d,
          creationTimestamp: d,
        });
      }

      if (Object.keys(inputData)?.includes(item?.questionCode)) {
        const quesId = item?.questionCode as string;
        delete item["sK"];

        if (quesId === cuisineType) {
          let res = values?.[cuisineType]?.[0]?.value;
          let ops = [values?.[cuisineType]?.[0]?.value];

          if (values?.secondary_region?.value) {
            res += "," + values?.secondary_region?.value;
            ops = [...ops, values?.secondary_region?.value];
          }
          if (
            values?.tertiary_region?.value &&
            values?.secondary_region?.value
          ) {
            res += "," + values?.tertiary_region?.value;
            ops = [...ops, values?.tertiary_region?.value];
          }
          data.push({
            ...item,
            response: res,
            optionsSelected: ops,
            creationDate: d,
            creationTimestamp: d,
          });
        } else if (quesId === "regional_staple_preference") {
          if (
            values?.["use_regional_staple"]?.value === "true" &&
            (values?.["regional_staple_preference"]?.length < 2 ||
              values?.["regional_staple_preference"]?.length > 5)
          ) {
            toast.error(
              "Please select at least 2 and at max 5 regional staple."
            );
            return;
          } else {
            let optionsSelected = values?.["regional_staple_preference"]?.map(
              (x: any) => x?.value
            );
            let response = values?.["regional_staple_preference"]
              ?.map((x: any) => x?.value)
              ?.toString();

            data.push({
              ...item,
              creationDate: d,
              creationTimestamp: d,
              optionsSelected: optionsSelected,
              response: response,
            });
          }
        } else if (
          quesId === "staple_preference_lunch" ||
          quesId === "staple_preference_dinner"
        ) {
          if (
            values?.[quesId]?.length > 0 &&
            (values?.[quesId]?.length < 2 || values?.[quesId]?.length > 5)
          ) {
            toast.error(
              `Please select at least 2 and at max 5 ${
                quesId === "staple_preference_lunch" ? "lunch" : "dinner"
              } staple.`
            );
            return;
          } else {
            let optionsSelected = values?.[quesId]?.map((x: any) => x?.value);
            let response = values?.[quesId]
              ?.map((x: any) => x?.value)
              ?.toString();

            data.push({
              ...item,
              creationDate: d,
              creationTimestamp: d,
              optionsSelected: optionsSelected,
              response: response,
            });
          }
        } else if (inputTypeText[quesId]) {
          data.push({
            ...item,
            response: values?.[quesId],
            creationDate: d,
            creationTimestamp: d,
          });
        } else if (inputTypeSingleSelect[quesId]) {
          data.push({
            ...item,
            optionsSelected: [values?.[quesId]?.value],
            response: values?.[quesId]?.value,
            creationDate: d,
            creationTimestamp: d,
          });
        } else {
          let optionsSelected = values?.[quesId]?.map((x: any) => x?.value);
          let response = values?.[quesId]
            ?.map((x: any) => x?.value)
            ?.toString();

          data.push({
            ...item,
            optionsSelected: optionsSelected,
            response: response,
            creationDate: d,
            creationTimestamp: d,
          });
        }
      }
    }

    if (!hasStateCuisineKey) {
      let d = moment().format("YYYY-MM-DD");

      data.push({
        response: JSON.stringify({
          state: values?.selected_state?.value ?? "",
          global: values?.selected_global?.value ?? "",
        }),
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: `states_${cuisineTypeRegion}`,
        title: allQuestionsMap["states_north"]?.title,
      });
    }

    if (!hasItemsToAvoidKey && values?.["items_to_avoid"]?.length > 0) {
      let d = moment().format("YYYY-MM-DD");
      let optionsSelected = values?.["items_to_avoid"]?.map(
        (x: any) => x?.value
      );
      let response = values?.["items_to_avoid"]
        ?.map((x: any) => x?.value)
        ?.toString();

      data.push({
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: `items_to_avoid`,
        title: allQuestionsMap["items_to_avoid"]?.title,
        optionsSelected: optionsSelected,
        response: response,
      });
    }

    if (
      !hasItemsToAvoidForVegKey &&
      values?.["items_to_avoid_for_veg"]?.length > 0
    ) {
      let d = moment().format("YYYY-MM-DD");
      let optionsSelected = values?.["items_to_avoid_for_veg"]?.map(
        (x: any) => x?.value
      );
      let response = values?.["items_to_avoid_for_veg"]
        ?.map((x: any) => x?.value)
        ?.toString();

      data.push({
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: `items_to_avoid_for_veg`,
        title: allQuestionsMap["items_to_avoid_for_veg"]?.title,
        optionsSelected: optionsSelected,
        response: response,
      });
    }

    if (
      !hasItemsToAvoidForNonVegKey &&
      values?.["items_to_avoid_for_non_veg"]?.length > 0
    ) {
      let d = moment().format("YYYY-MM-DD");
      let optionsSelected = values?.["items_to_avoid_for_non_veg"]?.map(
        (x: any) => x?.value
      );
      let response = values?.["items_to_avoid_for_non_veg"]
        ?.map((x: any) => x?.value)
        ?.toString();

      data.push({
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: `items_to_avoid_for_non_veg`,
        title: allQuestionsMap["items_to_avoid_for_non_veg"]?.title,
        optionsSelected: optionsSelected,
        response: response,
      });
    }

    if (!hasGutIssuesKey) {
      let d = moment().format("YYYY-MM-DD");
      if (values?.["gut_issues"]?.value) {
        data.push({
          optionsSelected: [values?.["gut_issues"]?.value],
          response: values?.["gut_issues"]?.value,
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "gut_issues",
          title: allQuestionsMap?.["gut_issues"]?.title,
        });
      }
    }
    if (!hasGutTriggersKey) {
      let d = moment().format("YYYY-MM-DD");
      if (values?.["gut_food_triggers"]?.value) {
        data.push({
          optionsSelected: [values?.["gut_food_triggers"]?.value],
          response: values?.["gut_food_triggers"]?.value,
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "gut_food_triggers",
          title: allQuestionsMap?.["gut_food_triggers"]?.title,
        });
      }
    }

    if (!hasWorkoutIntensityQuesKey) {
      let d = moment().format("YYYY-MM-DD");
      if (values?.["workout_intensity"]?.value) {
        data.push({
          optionsSelected: [values?.["workout_intensity"]?.value],
          response: values?.["workout_intensity"]?.value,
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "workout_intensity",
          title: "Workout intensity?",
        });
      }
    }
    if (!hasWorkoutTypeQuesKey) {
      let d = moment().format("YYYY-MM-DD");
      if (values?.["workout_type"]?.length > 0) {
        let optionsSelected = values?.["workout_type"]?.map(
          (x: any) => x?.value
        );
        let response = values?.["workout_type"]
          ?.map((x: any) => x?.value)
          ?.toString();

        data.push({
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: `workout_type`,
          title: "Workout type?",
          optionsSelected: optionsSelected,
          response: response,
        });
      }
    }

    if (!hasWorkoutDurationQuesKey) {
      let d = moment().format("YYYY-MM-DD");
      if (values?.["workout_duration"]?.value) {
        data.push({
          optionsSelected: [values?.["workout_duration"]?.value],
          response: values?.["workout_duration"]?.value,
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "workout_duration",
          title: "Workout duration?",
        });
      }
    }

    if (!hasMealPlanTypeKey) {
      let d = moment().format("YYYY-MM-DD");

      data.push({
        optionsSelected: values?.["mealplan_type_lunch"]?.value
          ? [values?.["mealplan_type_lunch"]?.value]
          : ["ELABORATE_MEALS_DINNER"],
        response:
          values?.["mealplan_type_lunch"]?.value || "ELABORATE_MEALS_DINNER",
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: "mealplan_type_lunch",
        title: allQuestionsMap["mealplan_type_lunch"]?.title,
      });

      data.push({
        optionsSelected: values?.["mealplan_type_dinner"]?.value
          ? [values?.["mealplan_type_dinner"]?.value]
          : ["ELABORATE_MEALS_DINNER"],
        response:
          values?.["mealplan_type_dinner"]?.value || "ELABORATE_MEALS_DINNER",
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: "mealplan_type_dinner",
        title: allQuestionsMap["mealplan_type_dinner"]?.title,
      });
    }
    if (!hasExtraCalorieKeys) {
      let d = moment().format("YYYY-MM-DD");

      [
        "extra_calorie_tea_coffee",
        "extra_calorie_protein_supplement",
        "extra_calorie_dessert_nightbinge",
      ].forEach((item: string) => {
        const quesKey = item as keyof typeof extraCalorieIntakeQuestionsTitle;

        data.push({
          optionsSelected: values?.[quesKey]?.value
            ? [values?.[quesKey]?.value]
            : ["0"],
          response: values?.[quesKey]?.value || "0",
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: quesKey,
          title: extraCalorieIntakeQuestionsTitle[quesKey],
        });
      });

      data.push({
        response: values?.["extra_calorie_others"] || "0",
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: "extra_calorie_others",
        title: extraCalorieIntakeQuestionsTitle["extra_calorie_others"],
        optionsSelected: null,
      });
    }

    if (!hasLowCarbMealKey) {
      let d = moment().format("YYYY-MM-DD");
      data.push({
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: "low_carb_meal",
        title: "Which meal you want as low carb meal?",
        optionsSelected: [values?.["low_carb_meal"]?.value] || [],
        response: values?.["low_carb_meal"]?.value || "",
      });
    }

    if (!hasConditionNutritionKey) {
      let optionsSelected = values?.["conditions_nutrition"]?.map(
        (x: any) => x?.value
      );
      let response = values?.["conditions_nutrition"]
        ?.map((x: any) => x?.value)
        ?.toString();

      let d = moment().format("YYYY-MM-DD");
      data.push({
        creationDate: d,
        creationTimestamp: d,
        userId: userId,
        questionCode: "conditions_nutrition",
        title: "Have you been diagnosed with any medical condition?",
        optionsSelected: optionsSelected,
        response: response,
      });
    }

    if (!hasPreWorkoutMealKey) {
      ["pre_workout_slot", "pre_workout_snacks"].forEach((item: any) => {
        let d = moment().format("YYYY-MM-DD");
        const quesID = item as keyof typeof allQuestionsMap;

        data.push({
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: item,
          title: allQuestionsMap?.[quesID]?.title,
          optionsSelected: [values?.[item]?.value] || [],
          response: values?.[item]?.value || "",
        });
      });
    }
    if (
      !hasRegionalStapleKey &&
      values?.["use_regional_staple"]?.value?.length > 0
    ) {
      if (
        values?.["use_regional_staple"]?.value === "true" &&
        (values?.["regional_staple_preference"]?.length < 2 ||
          values?.["regional_staple_preference"]?.length > 5)
      ) {
        toast.error("Please select at least 2 and at max 5 regional staple.");
        return;
      } else {
        let d = moment().format("YYYY-MM-DD");
        data.push({
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "use_regional_staple",
          title: allQuestionsMap?.["use_regional_staple"]?.title,
          optionsSelected: [values?.["use_regional_staple"]?.value] || [],
          response: values?.["use_regional_staple"]?.value || "",
        });

        let optionsSelected = values?.["regional_staple_preference"]?.map(
          (x: any) => x?.value
        );
        let response = values?.["regional_staple_preference"]
          ?.map((x: any) => x?.value)
          ?.toString();

        data.push({
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "regional_staple_preference",
          title: allQuestionsMap?.["regional_staple_preference"]?.title,
          optionsSelected: optionsSelected,
          response: response,
        });
      }
    }
    if (
      !hasStaplePreferenceLunchKey &&
      values?.["staple_preference_lunch"]?.length > 0
    ) {
      if (
        values?.["staple_preference_lunch"]?.length < 2 ||
        values?.["staple_preference_lunch"]?.length > 5
      ) {
        toast.error("Please select at least 2 and at max 5 lunch staple.");
        return;
      } else {
        let d = moment().format("YYYY-MM-DD");
        let optionsSelected = values?.["staple_preference_lunch"]?.map(
          (x: any) => x?.value
        );
        let response = values?.["staple_preference_lunch"]
          ?.map((x: any) => x?.value)
          ?.toString();

        data.push({
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "staple_preference_lunch",
          title: allQuestionsMap?.["staple_preference_lunch"]?.title,
          optionsSelected: optionsSelected,
          response: response,
        });
      }
    }
    if (
      !hasStaplePreferenceDinnerKey &&
      values?.["staple_preference_dinner"]?.length > 0
    ) {
      if (
        values?.["staple_preference_dinner"]?.length < 2 ||
        values?.["staple_preference_dinner"]?.length > 5
      ) {
        toast.error("Please select at least 2 and at max 5 dinner staple.");
        return;
      } else {
        let d = moment().format("YYYY-MM-DD");
        let optionsSelected = values?.["staple_preference_dinner"]?.map(
          (x: any) => x?.value
        );
        let response = values?.["staple_preference_dinner"]
          ?.map((x: any) => x?.value)
          ?.toString();

        data.push({
          creationDate: d,
          creationTimestamp: d,
          userId: userId,
          questionCode: "staple_preference_dinner",
          title: allQuestionsMap?.["staple_preference_dinner"]?.title,
          optionsSelected: optionsSelected,
          response: response,
        });
      }
    }

    saveAssessmentResponses(data);
  };

  const saveAssessmentResponses = async (data: any[]) => {
    try {
      const response = await makeRequest(
        assessmentService.saveNutritionAssessmentResponse(data, userId)
      );
      toast.success("Assessment response updated!");
    } catch (error: any) {
      toast.error("Error updating assessment!");
    }
  };

  const isIssueBasedGoal = () => {
    if (issueBasedGoals.includes(healthGoal)) {
      return true;
    }
    return false;
  };

  const enableIssueBasedPlanGeneration = () => {
    if (
      issueBasedPlanStatus === "APPROVED_FROM_DOCTOR" ||
      (issueBasedPlanStatus === "MEAL_PLAN_GENERATED" && mealPlanStartDate)
    ) {
      return true;
    }
    return false;
  };

  const regenerate = async (isRegenerated: boolean, startDate: string) => {
    let weeks = numberOfWeeks;

    let planStartDate = mealPlanStartDate;
    if (startDate !== "") {
      planStartDate = startDate;
    }

    let data = {
      userId: userId,
      goal: healthGoal,
      weeks: weeks,
      isRegenerated: isRegenerated,
      mealPlanStartDate: planStartDate,
    };

    const generateMealplan = async () => {
      try {
        const res = await makeRequest(
          nutritionService.saveMealPlan(data, userId)
        );
        toast.success("Meal plan regenerated!");
      } catch (error: any) {
        toast.error("Error regenerating plan!");
      }
    };

    generateMealplan();
  };

  const handleRetake = async (id: string) => {
    try {
      const response = await makeRequest(
        userService.handleUserRetakeAssement({
          id: id,
          updates: [
            {
              attributeName: "retakeAllowed",
              attributeValue: "true",
            },
          ],
        })
      );

      if (response?.data?.status === "SUCCESS") {
        toast.success("User will be able to retake the assessment now.");
        setRetakeAllowed(false);
      }
    } catch (error: any) {
      toast.error(error?.message);
    }
  };

  return (
    <div>
      <div
        className="row mx-0 py-4 px-3 rounded"
        style={{ backgroundColor: "white" }}
      >
        <div className="col-12">
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-start">
              <h5>Assesment Questions</h5>

              {isIssueBasedGoal() && (
                <h6 style={{ textTransform: "lowercase" }}>
                  {" - "}
                  {healthGoal}
                </h6>
              )}

              {issueBasedPlanStatus === "NEW_USER" && (
                <h6> ( Waiting for Doctor's approval! )</h6>
              )}
              {!mealPlanStartDate && (
                <span className="d-block ms-3 pb-2" style={{ color: "red" }}>
                  (Invalid meal plan start date, can't regenerate!)
                </span>
              )}
            </div>

            {isRetakeAssesmentButtonViewAllowed && (
              <button
                className="btn btn-sm btn-outline-success table-font"
                onClick={() => {
                  handleRetake(userId as string);
                }}
                disabled={!retakeAllowed || !mealPlanStartDate}
              >
                Allow retake assessment
              </button>
            )}
          </div>
          <div className="row mx-0 assessment-toggler">
            <div className="col-6 px-0">
              <button
                className={`w-100 btn ${viewFixed ? "active" : ""}`}
                onClick={() => setViewFixed(true)}
              >
                Fixed Questions
              </button>
            </div>
            <div className="col-6 px-0">
              <button
                className={`w-100 btn ${!viewFixed ? "active" : ""}`}
                onClick={() => setViewFixed(false)}
              >
                Editable Questions
              </button>
            </div>
          </div>
          {!viewFixed && (
            <div className="assessment-questions p-3">
              <form action="" onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Age
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={values?.age}
                    onChange={(e) => {
                      handleChange(e.target.value, "age");
                    }}
                    id=""
                    placeholder="Enter Age"
                    disabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Weight
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={values?.weight}
                    onChange={(e) => {
                      handleChange(e.target.value, "weight");
                    }}
                    id=""
                    placeholder="Enter Weight"
                    disabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Height
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={values?.height}
                    onChange={(e) => {
                      handleChange(e.target.value, "height");
                    }}
                    id=""
                    placeholder="Enter Height"
                    disabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Gender
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.gender}
                    onChange={(option: any) => {
                      handleChange(option, "gender");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="gender"
                    options={defaultOptions?.gender_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- How often do you workout?
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.how_often_nutrition}
                    onChange={(option: any) => {
                      handleChange(option, "how_often_nutrition");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="how_often_nutrition"
                    options={defaultOptions?.how_often_nutrition_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- What meals do you usually eat in a day?
                  </label>
                  <Select
                    isMulti={true}
                    value={values?.how_many_meals}
                    onChange={(option: any) => {
                      handleChange(option, "how_many_meals");
                    }}
                    name="how_many_meals"
                    options={defaultOptions?.how_many_meals_options}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- What is your food preference?
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.cuisine_types}
                    onChange={(option: any) => {
                      handleChange(option, "cuisine_types");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="cuisine_types"
                    options={defaultOptions?.cuisine_types_options}
                    isDisabled
                  />
                </div>
                {values?.items_to_avoid_for_veg?.length === 0 &&
                values?.items_to_avoid_for_non_veg?.length === 0 ? (
                  <div className="form-group mb-3">
                    <label htmlFor="" className="mb-1">
                      Q- What food items do you want to avoid?
                    </label>
                    <Select
                      isMulti={true}
                      value={values?.items_to_avoid}
                      onChange={(option: any) => {
                        handleChange(option, "items_to_avoid");
                      }}
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable={true}
                      isRtl={false}
                      isSearchable={true}
                      name="items_to_avoid"
                      options={defaultOptions?.items_to_avoid_options}
                      isDisabled={!isAssessmentQuestionEditAllowed}
                    />
                  </div>
                ) : (
                  <div className="form-group mb-3">
                    <label htmlFor="" className="mb-1">
                      Q- What food items do you want to avoid?
                    </label>
                    {values?.cuisine_types?.value === "veg" ||
                    values?.cuisine_types?.value === "egg" ? (
                      <Select
                        isMulti={true}
                        value={values?.items_to_avoid_for_veg}
                        onChange={(option: any) => {
                          handleChange(option, "items_to_avoid_for_veg");
                        }}
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable={true}
                        isRtl={false}
                        isSearchable={true}
                        name="items_to_avoid_for_veg"
                        options={defaultOptions?.items_to_avoid_for_veg_options}
                        isDisabled={!isAssessmentQuestionEditAllowed}
                      />
                    ) : (
                      <Select
                        isMulti={true}
                        value={values?.items_to_avoid_for_non_veg}
                        onChange={(option: any) => {
                          handleChange(option, "items_to_avoid_for_non_veg");
                        }}
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable={true}
                        isRtl={false}
                        isSearchable={true}
                        name="items_to_avoid_for_non_veg"
                        options={
                          defaultOptions?.items_to_avoid_for_non_veg_options
                        }
                        isDisabled={!isAssessmentQuestionEditAllowed}
                      />
                    )}
                  </div>
                )}
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- What regions cuisine do you usually eat?
                  </label>
                  <div className="row">
                    <div className="col-4">
                      <label htmlFor="" className="mb-1">
                        Primary
                      </label>
                      <Select
                        isMulti={false}
                        onChange={(option: any) => {
                          handleChange(option, cuisineType);
                        }}
                        value={values[cuisineType]}
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable={true}
                        isRtl={false}
                        isSearchable={true}
                        name={cuisineType}
                        options={defaultOptions?.veg_region_cuisine_options}
                        isDisabled
                      />
                    </div>
                    <div className="col-4">
                      <label htmlFor="" className="mb-1">
                        Secondary
                      </label>
                      <Select
                        isMulti={false}
                        onChange={(option: any) => {
                          handleChange(option, "secondary_region");
                        }}
                        value={values?.secondary_region}
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable={true}
                        isRtl={false}
                        isSearchable={true}
                        name={"secondary_region"}
                        options={defaultOptions?.veg_region_cuisine_options}
                        isDisabled={!isAssessmentQuestionEditAllowed}
                      />
                    </div>
                    <div className="col-4">
                      <label htmlFor="" className="mb-1">
                        Tertiary
                      </label>
                      <Select
                        isMulti={false}
                        onChange={(option: any) => {
                          handleChange(option, "tertiary_region");
                        }}
                        value={values?.tertiary_region}
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable={true}
                        isRtl={false}
                        isSearchable={true}
                        name={"tertiary_region"}
                        options={defaultOptions?.veg_region_cuisine_options}
                        isDisabled={
                          !isAssessmentQuestionEditAllowed ||
                          !values?.secondary_region?.value
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Choose one or more cuisines?
                  </label>
                  <div className="row">
                    <div className="col-6">
                      <Select
                        isMulti={false}
                        value={values?.selected_state}
                        onChange={(option: any) => {
                          handleChange(option, "selected_state");
                        }}
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable={true}
                        isRtl={false}
                        isSearchable={true}
                        name="selected_state"
                        options={
                          defaultOptions?.[
                            `states_${values[cuisineType]?.[0]?.value}_options`
                          ]
                        }
                        isDisabled={!isAssessmentQuestionEditAllowed}
                      />
                    </div>
                    <div className="col-6">
                      <Select
                        isMulti={false}
                        value={values?.selected_global}
                        onChange={(option: any) => {
                          handleChange(option, "selected_global");
                        }}
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable={true}
                        isRtl={false}
                        isSearchable={true}
                        name="selected_global"
                        options={defaultOptions?.[`global_region_options`]}
                        isDisabled={!isAssessmentQuestionEditAllowed}
                      />
                    </div>
                  </div>
                </div>
                {values?.cuisine_types?.value !== "veg" && (
                  <div className="form-group mb-3">
                    <label htmlFor="" className="mb-1">
                      Q- How often do you consume Poultry and Seafood?
                    </label>
                    <Select
                      isMulti={false}
                      value={values?.poultry_frequency}
                      onChange={(option: any) => {
                        handleChange(option, "poultry_frequency");
                      }}
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable={true}
                      isRtl={false}
                      isSearchable={true}
                      name="poultry_frequency"
                      options={defaultOptions?.poultry_frequency_options}
                      isDisabled={!isAssessmentQuestionEditAllowed}
                    />
                  </div>
                )}
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- What staples do you usually eat during lunch/ dinner?
                  </label>
                  <CreatableSelect
                    value={values?.which_staples}
                    onChange={(option: any) => {
                      handleChange(option, "which_staples");
                    }}
                    isMulti={true}
                    name="which_staples"
                    options={defaultOptions?.which_staples_options}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Have you been diagnosed with any medical condition?
                  </label>
                  <CreatableSelect
                    isMulti={true}
                    value={values?.conditions_nutrition}
                    onChange={(option: any) => {
                      handleChange(option, "conditions_nutrition");
                    }}
                    name="conditions_nutrition"
                    options={defaultOptions?.conditions_nutrition_options}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Any days in the week when you practice fasting?
                  </label>
                  <Select
                    isMulti={true}
                    value={values?.fasting_days}
                    onChange={(option: any) => {
                      handleChange(option, "fasting_days");
                    }}
                    name="fasting_days"
                    options={defaultOptions?.fasting_days_options}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Include dishes
                  </label>
                  <Select
                    isMulti
                    options={includeDishOptions}
                    value={selectedIncludeDishOptions}
                    onChange={(option: any) => {
                      handleChange(option, "include_dishes");
                      handleIncludeDishSelectChange(option);
                    }}
                    onInputChange={setSearchIncludeDishInput}
                    placeholder="Search and select dishes"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Include categories
                  </label>
                  <Select
                    isMulti
                    options={dishCategories}
                    value={selectedIncludeDishCategories}
                    onChange={(option: any) => {
                      handleChange(option, "include_categories");
                      handleIncludeDishCategorySelectChange(option);
                    }}
                    onInputChange={setSearchIncludeDishInput}
                    placeholder="Search and select categories"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Exclude dishes
                  </label>
                  <Select
                    isMulti
                    options={excludeDishOptions}
                    value={selectedExcludeDishOptions}
                    onChange={(option: any) => {
                      handleChange(option, "exclude_dishes");
                      handleExcludeDishSelectChange(option);
                    }}
                    onInputChange={setSearchExcludeDishInput}
                    placeholder="Search and select dishes"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Exclude categories
                  </label>
                  <Select
                    isMulti
                    options={dishCategories}
                    value={selectedExcludeDishCategories}
                    onChange={(option: any) => {
                      handleChange(option, "exclude_categories");
                      handleExcludeDishCategorySelectChange(option);
                    }}
                    onInputChange={setSearchExcludeDishInput}
                    placeholder="Search and select categories"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                {values?.cuisine_types?.value === "egg" && (
                  <div className="form-group mb-3">
                    <label htmlFor="" className="mb-1">
                      Q- Any days in the week when you don't consume egg?
                    </label>
                    <Select
                      isMulti={true}
                      value={values?.no_egg_days}
                      onChange={(option: any) => {
                        handleChange(option, "no_egg_days");
                      }}
                      name="no_egg_days"
                      options={defaultOptions?.fasting_days_options}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isDisabled={!isAssessmentQuestionEditAllowed}
                    />
                  </div>
                )}
                {values?.cuisine_types?.value === "nonveg" && (
                  <div className="form-group mb-3">
                    <label htmlFor="" className="mb-1">
                      Q- Any days in the week when you don't consume non-veg?
                    </label>
                    <Select
                      isMulti={true}
                      value={values?.no_nonveg_days}
                      onChange={(option: any) => {
                        handleChange(option, "no_nonveg_days");
                      }}
                      name="no_nonveg_days"
                      options={defaultOptions?.fasting_days_options}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isDisabled={!isAssessmentQuestionEditAllowed}
                    />
                  </div>
                )}
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Meal plan type Lunch
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.mealplan_type_lunch}
                    onChange={(option: any) => {
                      handleChange(option, "mealplan_type_lunch");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="mealplan_type_lunch"
                    options={defaultOptions?.mealplan_type_lunch_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Meal plan type Dinner
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.mealplan_type_dinner}
                    onChange={(option: any) => {
                      handleChange(option, "mealplan_type_dinner");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="mealplan_type_dinner"
                    options={defaultOptions?.mealplan_type_dinner_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Extra calories Tea/Coffee
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.extra_calorie_tea_coffee}
                    onChange={(option: any) => {
                      handleChange(option, "extra_calorie_tea_coffee");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="extra_calorie_tea_coffee"
                    options={defaultOptions?.extra_calorie_tea_coffee_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Extra calories Protein supplement
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.extra_calorie_protein_supplement}
                    onChange={(option: any) => {
                      handleChange(option, "extra_calorie_protein_supplement");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="extra_calorie_protein_supplement"
                    options={
                      defaultOptions?.extra_calorie_protein_supplement_options
                    }
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Extra calories Desserts/Night binge
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.extra_calorie_dessert_nightbinge}
                    onChange={(option: any) => {
                      handleChange(option, "extra_calorie_dessert_nightbinge");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="extra_calorie_dessert_nightbinge"
                    options={
                      defaultOptions?.extra_calorie_dessert_nightbinge_options
                    }
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Extra calories Others
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={500}
                    className="form-control"
                    value={
                      +values?.extra_calorie_others !== 0
                        ? values?.extra_calorie_others
                        : null
                    }
                    onChange={(e) => {
                      handleChange(e.target.value, "extra_calorie_others");
                    }}
                    id=""
                    placeholder="Enter extra calories"
                    disabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- When do you workout?
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.pre_workout_slot}
                    onChange={(option: any) => {
                      handleChange(option, "pre_workout_slot");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="pre_workout_slot"
                    options={defaultOptions?.pre_workout_slot_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Do you consume any pre-workout snacks?
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.pre_workout_snacks}
                    onChange={(option: any) => {
                      handleChange(option, "pre_workout_snacks");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="pre_workout_snacks"
                    options={defaultOptions?.pre_workout_snacks_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Which meal you want as low carb meal?
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.low_carb_meal}
                    onChange={(option: any) => {
                      handleChange(option, "low_carb_meal");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="low_carb_meal"
                    options={defaultOptions?.low_carb_meal_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                {healthGoal === "GUT_HEALTH" && (
                  <div className="form-group mb-3">
                    <label htmlFor="" className="mb-1">
                      Q- Water intake
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={values?.water_intake}
                      onChange={(e) => {
                        handleChange(e.target.value, "water_intake");
                      }}
                      id=""
                      placeholder="Enter water intake"
                      disabled={!isAssessmentQuestionEditAllowed}
                    />
                  </div>
                )}
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- {allQuestionsMap?.["gut_issues"]?.title}
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.gut_issues}
                    onChange={(option: any) => {
                      handleChange(option, "gut_issues");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="gut_issues"
                    options={defaultOptions?.gut_issues_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- {allQuestionsMap?.["gut_food_triggers"]?.title}
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.gut_food_triggers}
                    onChange={(option: any) => {
                      handleChange(option, "gut_food_triggers");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="gut_food_triggers"
                    options={defaultOptions?.gut_food_triggers_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Workout type?
                  </label>
                  <Select
                    isMulti={true}
                    value={values?.workout_type}
                    onChange={(option: any) => {
                      handleChange(option, "workout_type");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="workout_type"
                    options={defaultOptions?.workout_type_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Workout intensity?
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.workout_intensity}
                    onChange={(option: any) => {
                      handleChange(option, "workout_intensity");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="workout_intensity"
                    options={defaultOptions?.workout_intensity_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- Workout duration?
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.workout_duration}
                    onChange={(option: any) => {
                      handleChange(option, "workout_duration");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="workout_duration"
                    options={defaultOptions?.workout_duration_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- {allQuestionsMap?.["use_regional_staple"]?.title}
                  </label>
                  <Select
                    isMulti={false}
                    value={values?.use_regional_staple}
                    onChange={(option: any) => {
                      handleChange(option, "use_regional_staple");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="use_regional_staple"
                    options={defaultOptions?.use_regional_staple_options}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- {allQuestionsMap?.["regional_staple_preference"]?.title}{" "}
                    (Min: 2, Max: 5)
                  </label>
                  <Select
                    isMulti={true}
                    value={values?.regional_staple_preference}
                    onChange={(option: any) => {
                      handleChange(option, "regional_staple_preference");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="regional_staple_preference"
                    options={regionalStapleOptions}
                    isDisabled={
                      !isAssessmentQuestionEditAllowed ||
                      values?.use_regional_staple?.value !== "true"
                    }
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- {allQuestionsMap?.["staple_preference_lunch"]?.title}{" "}
                    (Min: 2, Max: 5)
                  </label>
                  <Select
                    isMulti={true}
                    value={values?.staple_preference_lunch}
                    onChange={(option: any) => {
                      handleChange(option, "staple_preference_lunch");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="staple_preference_lunch"
                    options={mealStapleOptions}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="" className="mb-1">
                    Q- {allQuestionsMap?.["staple_preference_dinner"]?.title}{" "}
                    (Min: 2, Max: 5)
                  </label>
                  <Select
                    isMulti={true}
                    value={values?.staple_preference_dinner}
                    onChange={(option: any) => {
                      handleChange(option, "staple_preference_dinner");
                    }}
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="staple_preference_dinner"
                    options={mealStapleOptions}
                    isDisabled={!isAssessmentQuestionEditAllowed}
                  />
                </div>
                <div className="form-group mb-3 text-center">
                  {isAssessmentQuestionEditAllowed && (
                    <button type="submit" className="btn btn-outline-secondary">
                      Update Responses
                    </button>
                  )}
                  {isRegenerateMealPlanAllowed && (
                    <button
                      type="button"
                      onClick={(e) => {
                        regenerate(true, "");
                      }}
                      className="btn btn-outline-secondary"
                      style={{ marginLeft: "10px" }}
                      disabled={!mealPlanStartDate}
                    >
                      Regenerate
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
          {viewFixed && (
            <div className="assessment-questions p-3">
              {allQuestions &&
                allQuestions?.length &&
                allQuestions?.map((item: any, index: any) => {
                  if (!Object.keys(inputData)?.includes(item?.questionCode)) {
                    if (item?.questionCode?.startsWith("snacks_")) {
                      return (
                        <div
                          className="form-group mb-3"
                          key={`question-${index}`}
                        >
                          <label htmlFor="" className="mb-1">
                            Q- {allQuestionsMap[item?.questionCode]?.title}
                          </label>
                          <p>{snacks?.join(", ")}</p>
                        </div>
                      );
                    } else if (item?.questionCode?.startsWith("breakfast_")) {
                      return (
                        <div
                          className="form-group mb-3"
                          key={`question-${index}`}
                        >
                          <label htmlFor="" className="mb-1">
                            Q- {allQuestionsMap[item?.questionCode]?.title}
                          </label>
                          <p>{breakfast?.join(", ")}</p>
                        </div>
                      );
                    } else if (item?.questionCode?.startsWith("lunch_")) {
                      return (
                        <div
                          className="form-group mb-3"
                          key={`question-${index}`}
                        >
                          <label htmlFor="" className="mb-1">
                            Q- {allQuestionsMap[item?.questionCode]?.title}
                          </label>
                          <p>{lunch?.join(", ")}</p>
                        </div>
                      );
                    } else if (item?.questionCode?.startsWith("dinner_")) {
                      return (
                        <div
                          className="form-group mb-3"
                          key={`question-${index}`}
                        >
                          <label htmlFor="" className="mb-1">
                            Q- {allQuestionsMap[item?.questionCode]?.title}
                          </label>
                          <p>{dinner?.join(", ")}</p>
                        </div>
                      );
                    }
                    return (
                      <div
                        className="form-group mb-3"
                        key={`question-${index}`}
                      >
                        <label htmlFor="" className="mb-1">
                          Q- {allQuestionsMap[item?.questionCode]?.title}
                        </label>
                        <p>{item?.response}</p>
                      </div>
                    );
                  }
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentQuestions;
